import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { ArrayHelper, Tools } from "@comunicare/comunicare-helper";
import {
  ALERT_STATUS,
  DataType,
  IDateInterval,
  IFilter,
  IObservationDefinition,
  IOComponent,
  WidgetName,
} from "@comunicare/comunicare-interfaces";
import moment from "moment";
import { merge, Subject, Subscription } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { RuleAlert } from "src/app/models/rule-alert.model";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { ObservationsService } from "src/app/providers/observations.service";
import { IRuleAlertsInfosParams, RuleAlertService } from "src/app/providers/rule-alert.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { AlertListDataSource } from "./patient-alert-list-datasource";

@Component({
  selector: "app-patient-alert-table",
  templateUrl: "./patient-alert-table.component.html",
  styleUrls: ["./patient-alert-table.component.scss"],
})
export class PatientAlertTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<RuleAlert>;
  @ViewChild(MatSort) sort: MatSort;

  @Input() alertLevel: number;
  @Input() widgetName: string;
  @Input() noSort = false;

  public dataTypeText = DataType.TEXT;

  public displayedColumns: string[] = ["name", "date", "values"];
  public dataSource: AlertListDataSource;
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public totalCount = 0;
  public loading = true;
  public fromDate: moment.Moment;
  public toDate: moment.Moment;
  private isInitialDateInterval = true;

  private availableServicesRefs: string[] = [];
  private monitoringIds: string[] = [];
  private refreshSub: Subscription;
  private refreshSub2: Subscription;
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();
  private definitions$: Subscription;
  public allDefinitions: IObservationDefinition[] = [];

  constructor(
    private ruleAlertService: RuleAlertService,
    private sessionService: SessionService,
    private userService: UserService,
    private healthcareService: HealthcareserviceService,
    private observationsService: ObservationsService
  ) {}

  ngOnInit(): void {
    this.dataSource = new AlertListDataSource(this.ruleAlertService);
    this.initDateFilter();
    this.initData();
    this.sessionService.refreshServerTraductions.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      // the idea is to make the pipes realize there's something that changed without
      // restarting everything. For that we just need to 'change' one of the
      // pipe variables.
      if (this.allDefinitions) {
        // Cloning will make it appear as if this is a new object
        this.allDefinitions = Tools.deepCopy(this.allDefinitions);
      }
    });
    this.refreshSub = this.sessionService.refreshOrangeAlertWidget.subscribe(() => {
      this.initData();
    });
    this.refreshSub2 = this.sessionService.refreshRedAlertWidget.subscribe(() => {
      this.initData();
    });
  }

  ngAfterViewInit(): void {
    if (!this.noSort) {
      this.sort.sortChange.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.paginator.pageIndex = 0;
      });
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadData(true)),
          takeUntil(this.onDestroy$)
        )
        .subscribe();
    } else {
      this.paginator.page
        .pipe(
          tap(() => this.loadData(true)),
          takeUntil(this.onDestroy$)
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
    this.refreshSub2?.unsubscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initData() {
    if (
      !this.sessionService.currentService ||
      !this.healthcareService.availableServices() ||
      this.healthcareService.availableServices().length < 1
    ) {
      return;
    }
    const departmentId = this.sessionService.currentService?.reference;
    this.availableServicesRefs = [departmentId];
    if (departmentId === this.sessionService.allsOption) {
      this.availableServicesRefs = this.healthcareService.availableServices().map((s) => s.asReference.reference);
    }
    this.monitoringIds = [];
    if (this.userService.isMonitoringUser) {
      const current = this.sessionService.currentMonitoringService;
      if (current?.reference === this.sessionService.allsOption) {
        this.monitoringIds = this.healthcareService.availableMonitoringServices().map((s) => s.asReference.reference);
      } else {
        this.monitoringIds = current ? [current.reference] : null;
      }
    }
    this.loadData(false);
  }

  private async loadData(noCount: boolean): Promise<void> {
    this.loading = true;
    const params: IRuleAlertsInfosParams = {
      pageNumber: this.paginator?.pageIndex ?? 0,
      pageSize: this.paginator?.pageSize ?? 5,
      filters: this.getFiltersFormatted(),
      sortId: this.sort?.active,
      sortOrder: this.sort?.direction,
      services: this.availableServicesRefs,
      monitoringServicesIds: this.monitoringIds?.length ? this.monitoringIds : undefined,
      alertLevels: [this.alertLevel],
      onlyNoDataTransmissionAlert: this.widgetName === WidgetName.PATIENT_ALERT_NO_TRANSMISSION,
      alertStatus: ALERT_STATUS.UNPROCESSED,
    };
    const alerts = await this.dataSource.loadData(params);
    if (!noCount) {
      this.totalCount = this.dataSource.getDataCount();
    }
    this.loading = false;

    this.definitions$?.unsubscribe();
    const loincs: string[] = alerts
      .map((a) => a.observation?.code.coding[0].code)
      .filter(ArrayHelper.onlyUnique)
      .filter((l) => l !== undefined);
    // There's no need to add the caremateId as a param of listDef since this is the home pages alert tile
    this.definitions$ = this.observationsService
      .listDef(undefined, loincs)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((defs) => {
        this.allDefinitions = this.keepOnlyUsefulDefinitions(defs, alerts);
      });
  }

  private getFiltersFormatted(): IFilter[] {
    const filters = Tools.deepCopy(this.dataSource.getAllFilters());
    filters.forEach((f) => {
      if (f.dataType === DataType.DATE) {
        f.data.fromDate = moment(f.data.fromDate).format("YYYY-MM-DD");
        f.data.toDate = moment(f.data.toDate).format("YYYY-MM-DD");
      }
    });
    return filters;
  }

  private keepOnlyUsefulDefinitions(obsDef: IObservationDefinition[], alerts: RuleAlert[]) {
    const allAlertsLoincs: string[] = [];
    alerts.forEach((alert) => {
      alert.valueComponents.forEach((vc) => {
        allAlertsLoincs.push(vc.code.coding[0].code);
      });
    });
    const definitions: IObservationDefinition[] = obsDef.filter((d) => d.components.find((c) => allAlertsLoincs.includes(c.loinc)));
    return definitions;
  }

  public get isNoneData(): boolean {
    return this.totalCount === 0;
  }

  public getValue(valueComponent: IOComponent): string {
    return (
      valueComponent.code.coding[0].display +
      ": " +
      valueComponent.valueQuantity.value +
      " " +
      (valueComponent.valueQuantity.unit ? valueComponent.valueQuantity.unit : "")
    );
  }

  public encodeToB64(str: string): string {
    return Base64Helper.utf8_to_b64(str);
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public applyFilter(filter: IFilter): void {
    this.dataSource.setFilter(filter);
    this.activeFilters = [...(this.dataSource?.getAllFilters() ?? [])];
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadData(false);
  }

  /** Default date range: from 3 months ago up to now. */
  private initDateFilter(): void {
    this.fromDate = moment().subtract(3, "month").startOf("day");
    this.toDate = moment().endOf("day");
    this.dataSource.setFilter(this.buildDateFilter());
  }

  private buildDateFilter(): IFilter {
    return {
      data: { fromDate: this.fromDate, toDate: this.toDate },
      propertyName: "date",
      dataType: DataType.DATE,
    };
  }

  /**
   * Triggered by the date interval selector above the table. The selector emits
   * its initial range on init, which we already applied in initDateFilter, so we
   * skip that first (redundant) emit to avoid a duplicate load.
   */
  public onDateIntervalChange(interval: IDateInterval): void {
    if (!interval?.fromDate || !interval?.toDate) {
      return;
    }
    this.fromDate = interval.fromDate;
    this.toDate = interval.toDate;
    if (this.isInitialDateInterval) {
      this.isInitialDateInterval = false;
      return;
    }
    this.applyFilter(this.buildDateFilter());
  }

  public clearFilter(): void {
    this.dataSource.clearFilter();
    this.activeFilters = [];
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadData(false);
  }
}
